<template>
  <div class="header-element notifications-dropdown">
    <a
      id="messageDropdown"
      href="#"
      role="button"
      class="header-link dropdown-toggle"
      aria-expanded="false"
      @click.stop.prevent="toggleNotifications"
    >
      <i class="fe fe-bell header-link-icon"></i>
      <span
        v-if="unreadCount > 0"
        id="notification-icon-badge"
        style="height: 18px"
        class="badge bg-secondary rounded-pill header-icon-badge pulse pulse-secondary"
      >
        {{ unreadCount }}
      </span>
    </a>
    <div
      v-click-outside="closeNotifications"
      class="main-header-dropdown dropdown-menu dropdown-menu-end"
      :class="{ show: showNotifications }"
      data-popper-placement="none"
    >
      <div class="p-3">
        <div class="d-flex align-items-center justify-content-between">
          <p class="mb-0 fs-17 fw-semibold">{{ $t('notifications.title') }}</p>
          <span
            v-if="unreadCount > 0"
            id="notifiation-data"
            class="fs-10 px-1 bg-secondary rounded-pill"
          >
            {{ unreadCount }} {{ $t('notifications.unread') }}
          </span>
        </div>
      </div>
      <div class="dropdown-divider"></div>
      <simplebar data-simplebar-auto-hide="false" style="max-height: 350px">
        <ul v-if="notifications.length" id="header-notification-scroll" class="list-unstyled mb-0">
          <li v-for="(item, key) in notifications" :key="key" class="p-2 text-wrap border-bottom">
            <div>
              <NotificationCard
                :notification="item"
                @view-detail="openNotificationDetail"
                @remove="removeNotification"
              />
            </div>
          </li>
        </ul>
      </simplebar>
      <div v-if="notifications.length" class="p-3 empty-header-item1 border-top">
        <div class="d-grid">
          <a href="javascript:void(0);" class="btn btn-primary">{{
            $t('notifications.view_all')
          }}</a>
        </div>
      </div>
      <div v-if="!notifications.length" class="p-5 empty-item1">
        <div class="text-center">
          <span class="avatar avatar-xl avatar-rounded bg-secondary-transparent">
            <i class="ri-notification-off-line fs-2"></i>
          </span>
          <h6 class="fw-semibold mt-3">{{ $t('notifications.no_notifications') }}</h6>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NotificationCard from '@/components/common/NotificationCard.vue'
import { authStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notifications'
import simplebar from 'simplebar-vue'
import { io } from 'socket.io-client'
import { useRouter } from 'vue-router'

export default {
  name: 'NotificationWidget',
  components: {
    NotificationCard,
    simplebar
  },
  emits: ['open-notification-detail'],
  setup() {
    const notificationStore = useNotificationStore()
    const router = useRouter()
    return { notificationStore, router }
  },
  data() {
    return {
      socket: null,
      joinedNamespace: null,
      showNotifications: false,
      destroyed: false,
      stats: { reconnects: 0, connectErrors: 0, lastError: null },
      socketBaseUrl: null
    }
  },
  computed: {
    notifications() {
      return this.notificationStore.notifications
    },
    unreadCount() {
      return this.notificationStore.unreadCount
    }
  },
  async created() {
    this.auth = authStore()
    const user = await this.waitForUser()
    if (this.destroyed) return

    this.notificationStore.fetchUnreadNotifications()
    this.setupSocket()
    if (user) this.connect()
  },
  beforeUnmount() {
    this.destroyed = true
    this.teardownSocket()
  },
  methods: {
    waitForUser(timeoutMs = 5000) {
      return new Promise((resolve) => {
        const start = Date.now()
        const t = setInterval(() => {
          const u = this.auth?.loggedUser
          if (u?.uuid || u?.user_uuid) {
            clearInterval(t)
            resolve(u)
          } else if (Date.now() - start > timeoutMs) {
            clearInterval(t)
            resolve(null)
          }
        }, 100)
      })
    },
    getUserUuid() {
      return this.auth?.loggedUser?.uuid || this.auth?.loggedUser?.user_uuid || null
    },
    getToken() {
      return this.auth?.accessToken || localStorage.getItem('accessToken') || null
    },
    setupSocket() {
      const rawBaseUrl =
        import.meta.env.VITE_SOCKET_URL ||
        import.meta.env.VITE_API_URL_PROTOCOLS ||
        import.meta.env.VITE_API_BASE_URL ||
        null
      this.socketBaseUrl = rawBaseUrl ? this.normalizeSocketBaseUrl(rawBaseUrl) : null
    },
    normalizeSocketBaseUrl(rawUrl) {
      if (!rawUrl) return null
      let cleaned = String(rawUrl).trim().replace(/\/$/, '')
      if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(cleaned)) cleaned = `http://${cleaned}`
      try {
        const url = new URL(cleaned)
        if (url.protocol === 'ws:') url.protocol = 'http:'
        if (url.protocol === 'wss:') url.protocol = 'https:'
        if (
          typeof window !== 'undefined' &&
          window.location?.protocol === 'https:' &&
          url.protocol === 'http:'
        ) {
          url.protocol = 'https:'
        }
        return url.toString().replace(/\/$/, '')
      } catch (e) {
        return cleaned
      }
    },
    connect() {
      const userUuid = this.getUserUuid()
      if (!userUuid || !this.socketBaseUrl) {
        console.warn('[NotificationWidget] Socket deshabilitado: falta uuid o base URL')
        return
      }
      if (this.socket && this.joinedNamespace === userUuid && this.socket.connected) return

      this.teardownSocket()

      const namespaceUrl = `${this.socketBaseUrl}/${userUuid}`

      this.socket = io(namespaceUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 20000,
        auth: { token: this.getToken() }
      })

      const s = this.socket

      s.on('connect', () => {
        this.stats.reconnects += 1
        console.log('[NotificationWidget] Socket connected:', namespaceUrl)
        this.socket.emit('join_room', { room: userUuid })
        this.joinedNamespace = userUuid
      })

      s.on('disconnect', (reason) => {
        console.warn('[NotificationWidget] Socket disconnected:', reason)
        if (reason === 'io server disconnect') {
          s.connect()
        }
      })

      s.on('connect_error', (error) => {
        this.stats.connectErrors += 1
        this.stats.lastError = error?.message || String(error)
        if (this.socket && this.socket.active) {
          console.warn(
            '[NotificationWidget] Socket connection error (reintentando):',
            this.stats.lastError
          )
        } else {
          console.error(
            '[NotificationWidget] Conexión denegada por el servidor:',
            this.stats.lastError
          )
        }
        const msg = this.stats.lastError || ''
        if (msg.includes('jwt') || msg.includes('auth') || msg.includes('unauthorized')) {
          if (typeof this.auth.refreshAccessToken === 'function') {
            this.auth.refreshAccessToken().finally(() => {
              if (!this.destroyed && this.socket) {
                this.socket.auth = { token: this.getToken() }
              }
            })
          }
        }
      })

      s.on('reconnect_failed', () => {
        console.error('[NotificationWidget] Reintentos de reconexión agotados', this.stats)
      })

      s.on('notification', (data) => this.addNotification(data))
      s.on('new_notification', (data) => this.addNotification(data))

      s.connect()
    },
    teardownSocket() {
      if (!this.socket) return
      const s = this.socket
      try {
        s.removeAllListeners()
      } catch (e) {
        console.debug('[NotificationWidget] removeAllListeners ignorado:', e)
      }
      try {
        s.disconnect()
      } catch (e) {
        console.debug('[NotificationWidget] disconnect ignorado:', e)
      }
      this.socket = null
      this.joinedNamespace = null
    },
    addNotification(data) {
      this.notificationStore.addNotification(data, this.$i18n.locale)
    },
    openNotificationDetail(notification) {
      const isChain = notification.type && notification.type.includes('chain')
      const isCorrelative = notification.type && notification.type.includes('correlative')

      if (isChain || isCorrelative) {
        this.removeNotification(notification)

        let highlightId =
          notification.correlative_uui ||
          notification.correlative_uuid ||
          (notification.correlative
            ? notification.correlative.uui || notification.correlative.uuid
            : null)

        if (!highlightId) {
          highlightId =
            notification.project_document_uuid ||
            (notification.correlative ? notification.correlative.project_document_uuid : null)
        }

        const queryParams = {}
        if (highlightId) queryParams.highlight = highlightId

        if (notification.metadata) {
          Object.assign(queryParams, notification.metadata)
        } else if (notification.correlative && notification.correlative.project_document) {
          const pd = notification.correlative.project_document
          const project = pd.project
          if (project) {
            if (project.business_unit?.uuid)
              queryParams.business_unit_uuid = project.business_unit.uuid
            if (project.code) queryParams.ceco = project.code
          }
        }

        if (
          highlightId ||
          (notification.metadata && Object.keys(notification.metadata).length > 0)
        ) {
          this.router.push({
            path: '/quality/answers',
            query: queryParams
          })
        }

        this.closeNotifications()
      } else {
        this.$emit('open-notification-detail', notification)
      }
    },
    removeNotification(item) {
      this.notificationStore.markAsRead(item.id)
    },
    toggleNotifications() {
      this.showNotifications = !this.showNotifications
    },
    closeNotifications() {
      this.showNotifications = false
    }
  }
}
</script>

<style scoped>
.notifications-dropdown {
  position: relative;
}
.main-header-dropdown {
  position: absolute;
  inset: 40px 0px auto auto !important;
  margin: 0px;
  display: none;
  z-index: 1000;
}
.main-header-dropdown.show {
  display: block;
}
</style>
